'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ChevronLeft, ArrowRight, User, Car, MessageSquare, Eye, EyeOff } from 'lucide-react';
import type { ClientDetails } from './BookingWizard';

interface Props {
  onSubmit: (details: ClientDetails) => void;
  onBack: () => void;
  initial: ClientDetails | null;
}

type FormValues = ClientDetails & { password?: string };

export default function StepYourDetails({ onSubmit, onBack, initial }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: initial ?? {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      vehicleColor: '',
      notes: '',
      createAccount: false,
    },
  });

  const createAccount = watch('createAccount');

  const onFormSubmit = (data: FormValues) => {
    onSubmit({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      vehicleMake: data.vehicleMake,
      vehicleModel: data.vehicleModel,
      vehicleYear: data.vehicleYear,
      vehicleColor: data.vehicleColor,
      notes: data.notes,
      createAccount: data.createAccount,
    });
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-foreground mb-2">Your Details</h1>
        <p className="text-muted-foreground">Tell us about yourself and your vehicle so we can prepare for your appointment.</p>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {/* Personal info */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <User size={14} className="text-primary" />
            </div>
            <h2 className="text-sm font-bold text-foreground">Contact Information</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="firstName">
                First Name <span className="text-red-400">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                {...register('firstName', { required: 'First name is required' })}
                className={`w-full px-3 py-2.5 rounded-lg bg-background border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                  errors.firstName ? 'border-red-500/60' : 'border-input hover:border-border'
                }`}
                placeholder="Marcus"
              />
              {errors.firstName && <p className="text-xs text-red-400 mt-1">{errors.firstName.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="lastName">
                Last Name <span className="text-red-400">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                {...register('lastName', { required: 'Last name is required' })}
                className={`w-full px-3 py-2.5 rounded-lg bg-background border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                  errors.lastName ? 'border-red-500/60' : 'border-input hover:border-border'
                }`}
                placeholder="Rivera"
              />
              {errors.lastName && <p className="text-xs text-red-400 mt-1">{errors.lastName.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="email">
                Email Address <span className="text-red-400">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Confirmation and reminders sent here</p>
              <input
                id="email"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
                })}
                className={`w-full px-3 py-2.5 rounded-lg bg-background border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                  errors.email ? 'border-red-500/60' : 'border-input hover:border-border'
                }`}
                placeholder="you@example.com"
              />
              {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="phone">
                Phone Number <span className="text-red-400">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">SMS reminders sent here</p>
              <input
                id="phone"
                type="tel"
                {...register('phone', {
                  required: 'Phone number is required',
                  pattern: { value: /^\+?[\d\s\-()]{10,}$/, message: 'Enter a valid phone number' },
                })}
                className={`w-full px-3 py-2.5 rounded-lg bg-background border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                  errors.phone ? 'border-red-500/60' : 'border-input hover:border-border'
                }`}
                placeholder="(305) 555-0192"
              />
              {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone.message}</p>}
            </div>
          </div>
        </div>

        {/* Vehicle info */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Car size={14} className="text-primary" />
            </div>
            <h2 className="text-sm font-bold text-foreground">Vehicle Information</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="vehicleYear">
                Year <span className="text-red-400">*</span>
              </label>
              <input
                id="vehicleYear"
                type="text"
                {...register('vehicleYear', { required: 'Vehicle year is required' })}
                className={`w-full px-3 py-2.5 rounded-lg bg-background border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                  errors.vehicleYear ? 'border-red-500/60' : 'border-input hover:border-border'
                }`}
                placeholder="2022"
              />
              {errors.vehicleYear && <p className="text-xs text-red-400 mt-1">{errors.vehicleYear.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="vehicleMake">
                Make <span className="text-red-400">*</span>
              </label>
              <input
                id="vehicleMake"
                type="text"
                {...register('vehicleMake', { required: 'Vehicle make is required' })}
                className={`w-full px-3 py-2.5 rounded-lg bg-background border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                  errors.vehicleMake ? 'border-red-500/60' : 'border-input hover:border-border'
                }`}
                placeholder="BMW"
              />
              {errors.vehicleMake && <p className="text-xs text-red-400 mt-1">{errors.vehicleMake.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="vehicleModel">
                Model <span className="text-red-400">*</span>
              </label>
              <input
                id="vehicleModel"
                type="text"
                {...register('vehicleModel', { required: 'Vehicle model is required' })}
                className={`w-full px-3 py-2.5 rounded-lg bg-background border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                  errors.vehicleModel ? 'border-red-500/60' : 'border-input hover:border-border'
                }`}
                placeholder="M3 Competition"
              />
              {errors.vehicleModel && <p className="text-xs text-red-400 mt-1">{errors.vehicleModel.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="vehicleColor">
                Color
              </label>
              <input
                id="vehicleColor"
                type="text"
                {...register('vehicleColor')}
                className="w-full px-3 py-2.5 rounded-lg bg-background border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all hover:border-border"
                placeholder="Portimao Blue"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <MessageSquare size={14} className="text-primary" />
            </div>
            <h2 className="text-sm font-bold text-foreground">Additional Notes</h2>
          </div>
          <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="notes">
            Special requests or concerns
          </label>
          <p className="text-xs text-muted-foreground mb-2">Paint scratches, pet hair, specific areas of concern, etc.</p>
          <textarea
            id="notes"
            rows={3}
            {...register('notes')}
            className="w-full px-3 py-2.5 rounded-lg bg-background border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all hover:border-border resize-none"
            placeholder="Deep scratch on rear bumper, heavy dog hair on back seats..."
          />
        </div>

        {/* Create account option */}
        <div className="bg-card border border-border rounded-xl p-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              {...register('createAccount')}
              className="mt-0.5 w-4 h-4 rounded border-border accent-primary"
            />
            <div>
              <p className="text-sm font-semibold text-foreground">Create a Precision Surface & Detail account</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Save your vehicle info and view/manage all your bookings in one place. Optional — you can book as a guest.
              </p>
            </div>
          </label>

          {createAccount && (
            <div className="mt-4 pt-4 border-t border-border animate-slide-up">
              <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="password">
                Create Password <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', createAccount ? {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Minimum 8 characters' },
                  } : {})}
                  className="w-full px-3 py-2.5 pr-10 rounded-lg bg-background border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  placeholder="Min. 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-5 py-3 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 active:scale-95"
          >
            <ChevronLeft size={16} />
            Back
          </button>
          <button
            type="submit"
            className="flex items-center gap-2 px-8 py-3 rounded-xl gold-gradient text-primary-foreground text-base font-bold transition-all duration-150 hover:opacity-90 active:scale-95 shadow-gold"
          >
            Continue to Payment
            <ArrowRight size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}